import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Reset from '../../components/User/Reset';
import { useAuthStore } from '../../store/authStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';
import {
  takePostLoginRedirectTarget,
  type PostLoginRedirect,
} from '../../utils/pendingSearchRedirectStorage';
import { UNCONFIRMED_USER_MESSAGE } from '../../utils/resendConfirmationEmail';
import ResendConfirmationEmailButton from '../../components/SignUp/ResendConfirmationEmailButton';
import { validateEmail as validateEmailFormat } from '../../utils/validations';
import BackgroundPages from '@/global/Background/BackgroundPages';
import useText from '@/hooks/useText';

function validateEmail(email: string) {
  const normalized = email.trim().replace(/\s+/g, '');
  return validateEmailFormat(normalized).isValid;
}

function encodeRenewSurveyToken(value: string) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type LoginProps = {
  onLoginSuccess?: () => void;
  onClose?: () => void;
  hideCloseButton?: boolean;
};

function Login({ onLoginSuccess }: LoginProps = {}) {
  const navigate = useNavigate();

  const { user, login, logout, isAuthenticating, authError } = useAuthStore();
  const isAuthenticated = !!user;

  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [reset, setReset] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [renewSurveyUrl, setRenewSurveyUrl] = useState('/feedback-survey?type=renewal');
  const title = useText('home.hero.title');
  const titleHighlight = useText('home.hero.title_highlight');

  const loginJustSucceededRef = useRef(false);
  const postLoginTargetRef = useRef<PostLoginRedirect | null>(null);
  const postLoginFallbackHrefRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || isLoading || isAuthenticating) {
      return;
    }
    if (onLoginSuccess) {
      onLoginSuccess();
      return;
    }
    if (!loginJustSucceededRef.current) {
      return;
    }
    loginJustSucceededRef.current = false;
    const target = postLoginTargetRef.current ?? '/';
    postLoginTargetRef.current = null;
    navigate(target, { replace: true });
  }, [isAuthenticated, navigate, onLoginSuccess, isLoading, isAuthenticating]);

  useEffect(() => {
    if (authError) {
      setErrorMessage(authError);
    }
  }, [authError]);

  useEffect(() => {
    if (errorMessage !== 'User is disabled.' || !isEmailValid) {
      setRenewSurveyUrl('/feedback-survey?type=renewal');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const rt = encodeRenewSurveyToken(normalizedEmail);
    setRenewSurveyUrl(`/feedback-survey?type=renewal&rt=${encodeURIComponent(rt)}`);
  }, [email, errorMessage, isEmailValid]);

  if (isAuthenticated) {
    return (
      <div className="container txt-contenido">
        <div className="login-container mb-5">
          <div className="signin-container">
            <div className="col-12 text-center">
              <h3 className="login-title" style={{ color: 'white' }}>
                Already Logged In
              </h3>
              <p style={{ color: 'white', marginBottom: '20px' }}>
                You are already authenticated. You can logout to test the login form.
              </p>
              <div className="container-contact100-form-btn">
                <div className="wrap-contact100-form-btn">
                  <div className="contact100-form-bgbtn" />
                  <button
                    className="contact100-form-btn"
                    onClick={async () => {
                      await logout();
                      window.location.reload();
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <Link to="/" style={{ color: 'white' }}>
                  Go to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const togglePassVisibility = () => {
    setIsPassVisible(prevState => !prevState);
  };

  const handleChangeEmail = event => {
    const emailValue = event.target.value.trim().replace(/\s+/g, '');
    setEmail(emailValue);
    setIsEmailValid(validateEmail(emailValue));
  };


  const handleChangePassword = event => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);
  };

  const handleSubmit = async evt => {
    evt.preventDefault();
    setErrorMessage('');

    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }

    if (!isEmailValid) {
      setErrorMessage('Please enter a valid email');
      return;
    }

    try {
      setIsLoading(true);
      await login({ username: email, password });

      if (onLoginSuccess) {
        setTimeout(() => onLoginSuccess(), 100);
      } else {
        const target = takePostLoginRedirectTarget();
        postLoginTargetRef.current = target;
        postLoginFallbackHrefRef.current =
          typeof target === 'string' ? target : `${target.pathname}${target.search}`;
        loginJustSucceededRef.current = true;

        setTimeout(() => {
          if (window.location.pathname === '/login') {
            const href = postLoginFallbackHrefRef.current ?? '/';
            postLoginFallbackHrefRef.current = null;
            window.location.href = href;
          }
        }, 1000);
      }
    } catch (err) {
      console.error('🔐 Login failed:', err);
      const typedError = err as { message: string };
      setErrorMessage(typedError.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = evt => {
    evt.preventDefault();
    setReset(true);
  };

  if (reset) {
    return (
      <div className="apply-trial-page">
        <BackgroundPages />
        <div className="apply-trial-content">
          <Reset setReset={setReset} />
        </div>
      </div>
    );
  }

  return (
    <div className="apply-trial-page">
      <BackgroundPages />
      <div className="apply-trial-content login-split-wrap">
        {(isLoading || isAuthenticating) && (
          <div className="loading-overlay">
            <div className="loading" />
          </div>
        )}
        <div className="login-split">
          <div className="login-split-copy">
            <h1 className="home-hero-title">
              {title}
              <span className="home-hero-highlight">{titleHighlight}</span>
            </h1>
            <p className="login-split-lead">
              Sign in to run advanced queries, save projects and access the full curated disease–gene–variant
              knowledge base.
            </p>
            <div className="login-mini-stats">
              <div className="login-mini-stat">
                <div className="login-mini-stat-value">50K+</div>
                <div className="login-mini-stat-label">Diseases</div>
              </div>
              <div className="login-mini-stat">
                <div className="login-mini-stat-value">30K+</div>
                <div className="login-mini-stat-label">Genes</div>
              </div>
              <div className="login-mini-stat">
                <div className="login-mini-stat-value">1.7M+</div>
                <div className="login-mini-stat-label">Variants</div>
              </div>
            </div>
          </div>

          <div className="login-card-col">
            <div className="login-card">
            <h3 className="login-card-title">Log in</h3>
            <p className="login-card-subtitle">Welcome back. Enter your credentials to continue.</p>
            <form onSubmit={handleSubmit}>
              <div className="text-left w-full" data-validate="Email is required">
                <label className="login-card-label" htmlFor="email">
                  Email
                </label>
                <input
                  className="form-input"
                  id="email"
                  type="text"
                  name="email"
                  value={email}
                  onChange={handleChangeEmail}
                  placeholder="you@institution.org"
                  required
                />
              </div>
              <div className="text-left w-full mt-4" data-validate="Password is required">
                <div className="login-card-label-row">
                  <label className="login-card-label" htmlFor="password">
                    Password
                  </label>
                  <a
                    href="true"
                    onClick={evt => {
                      handleReset(evt);
                    }}
                    className="login-forgot"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="login-password-wrap">
                  <input
                    onChange={handleChangePassword}
                    name="password"
                    id="password"
                    type={`${isPassVisible ? 'text' : 'password'}`}
                    className="form-input"
                    placeholder="Password"
                  />
                  <button type="button" className="eye-icon" onClick={togglePassVisibility}>
                    {isPassVisible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="mt-5">
                <SharedButton type="submit" variant="primary" className="w-full">
                  Login
                </SharedButton>
              </div>
              {errorMessage ? (
                <div className="form-error bg-white p-4 w-full mx-auto text-center rounded-xl border border-red-900 text-red-900 mt-4">
                  <span className="flex flex-col items-center justify-center gap-2 text-sm">
                    <ExclamationCircleIcon className="w-5 h-5 mr-1" />
                    {errorMessage}
                  </span>
                  <br />
                  {errorMessage === 'User is disabled.' ? (
                    <div className="text-black text-xs font-light mt-2">
                      If you want to renew your account, please complete the{' '}
                      <a href={renewSurveyUrl} className="text-pink hover:underline font-semibold">
                        Renewal Survey
                      </a>{' '}
                      and confirm your account renewal{' '}
                    </div>
                  ) : null}
                  {errorMessage === UNCONFIRMED_USER_MESSAGE ? (
                    <ResendConfirmationEmailButton email={email} />
                  ) : null}
                </div>
              ) : null}
            </form>
            <div className="login-or">OR</div>
            <SharedButton variant="secondaryOutline" className="w-full" onClick={() => navigate('/sso')}>
              SSO Login
            </SharedButton>
            <div className="login-card-footer">
              Don't have an account? <Link to="/plans">See plans</Link>
            </div>
          </div>
          <p className="login-encryption-note">Protected by enterprise-grade encryption</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
